import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { Exact } from "@Helpers/types/exact";
import type { TakeoffData, TakeoffListItemAttached } from "@ServerTypes/takeoffs/takeoffs.schema";
import type { MeasurementItem } from "@ServerTypes/measurements/measurements.schema";

export interface MeasurementModalStatus {
  status: boolean;
}

const measurementsListSlice = createSlice({
  name: "measurementsList",
  initialState: [] as MeasurementItem[],
  reducers: {
    setMeasurementsData: (_state: MeasurementItem[], action: PayloadAction<MeasurementItem[] | undefined>) =>
      action.payload ?? [],
  },
});

const takeoffsListSlice = createSlice({
  name: "takeoffsList",
  initialState: [] as TakeoffListItemAttached[],
  reducers: {
    setTakeoffsList: (
      _state: TakeoffListItemAttached[],
      action: PayloadAction<TakeoffListItemAttached[] | undefined>,
    ) => (action.payload as TakeoffListItemAttached[]) ?? [],
  },
});

const takeoffDataSlice = createSlice({
  name: "takeoffData",
  initialState: {} as Partial<TakeoffData>,
  reducers: {
    setTakeoff: (_state: Partial<TakeoffData>, action: PayloadAction<Partial<TakeoffData>>) => action.payload,
  },
});

const measurementsModalStatusSlice = createSlice({
  name: "measurementsModalStatus",
  initialState: { status: false } as MeasurementModalStatus,
  reducers: {
    setMeasurementsModalStatus: (_state: MeasurementModalStatus, action: PayloadAction<MeasurementModalStatus>) =>
      action.payload,
  },
});

const isMeasurementStartedSlice = createSlice({
  name: "isMeasurementStarted",
  initialState: { status: false } as MeasurementModalStatus,
  reducers: {
    setIsMeasurementStarted: (_state: MeasurementModalStatus, action: PayloadAction<MeasurementModalStatus>) =>
      action.payload,
  },
});

const { setTakeoff: _setTakeoffRaw } = takeoffDataSlice.actions;
const { setMeasurementsModalStatus: _setMeasurementsModalStatusRaw } = measurementsModalStatusSlice.actions;
const { setIsMeasurementStarted: _setIsMeasurementStartedRaw } = isMeasurementStartedSlice.actions;

/**
 * `setMeasurementsData` and `setTakeoffsList` are exported raw — all call sites
 * pass API response data (already strictly typed by the response schema), and
 * `ExactArrayItems<>` produces false positives on widened response array types
 * since the source array doesn't bear the per-element Exact brand.
 */
export const { setMeasurementsData } = measurementsListSlice.actions;
export const { setTakeoffsList } = takeoffsListSlice.actions;

/**
 * Object-payload action creators are wrapped with `Exact<>` so excess properties
 * are caught even when the payload is passed through a variable.
 */
export const setTakeoff = <T extends Exact<Partial<TakeoffData>, T>>(payload: T) => _setTakeoffRaw(payload);
export const setMeasurementsModalStatus = <T extends Exact<MeasurementModalStatus, T>>(payload: T) =>
  _setMeasurementsModalStatusRaw(payload);
export const setIsMeasurementStarted = <T extends Exact<MeasurementModalStatus, T>>(payload: T) =>
  _setIsMeasurementStartedRaw(payload);

export const measurementsList = measurementsListSlice.reducer;
export const takeoffsList = takeoffsListSlice.reducer;
export const takeoffData = takeoffDataSlice.reducer;
export const measurementsModalStatus = measurementsModalStatusSlice.reducer;
export const isMeasurementStarted = isMeasurementStartedSlice.reducer;
